import type { LucideIcon } from 'lucide-react'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  FileText,
  PlayCircle,
  SendHorizonal,
  Sparkles,
} from 'lucide-react'
import type { CaseEvent } from '@/lib/cases'
import { labelForDocumentType } from '@/features/case-workspace/mappings'

export type ActivityTone = 'neutral' | 'info' | 'success' | 'warning'

export type ActivityPresentation = {
  title: string
  description: string
  category: string
  tone: ActivityTone
  icon: LucideIcon
  actorLabel?: string
}

function getString(payload: Record<string, unknown>, key: string) {
  const value = payload[key]
  return typeof value === 'string' && value.trim() ? value : undefined
}

function getNumber(payload: Record<string, unknown>, key: string) {
  const value = payload[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function humanizeDocumentType(documentType?: string) {
  if (!documentType) return 'document'

  try {
    return labelForDocumentType(documentType as Parameters<typeof labelForDocumentType>[0])
  } catch {
    return documentType.replace(/_/g, ' ')
  }
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function actorLabel(payload: Record<string, unknown>) {
  return getString(payload, 'actor_label')
}

function reviewSummary(payload: Record<string, unknown>) {
  const openCount = getNumber(payload, 'open_findings')
  const highCount = getNumber(payload, 'high_priority_findings')

  if (openCount == null) {
    return 'AWA began reviewing the case and will surface anything that may need follow up.'
  }

  if (openCount === 0) {
    return 'AWA began reviewing the case and did not identify any open issues at the start of review.'
  }

  const openText = `${pluralize(openCount, 'item')} ${openCount === 1 ? 'needs' : 'need'} attention`
  if (!highCount) {
    return `AWA began reviewing the case. ${openText}.`
  }

  return `AWA began reviewing the case. ${openText}, including ${pluralize(highCount, 'high priority issue')}.`
}

export function presentCaseEvent(event: CaseEvent): ActivityPresentation {
  const payload = event.payload ?? {}
  const actor = actorLabel(payload)

  switch (event.kind) {
    case 'case.created': {
      const title = getString(payload, 'title')
      return {
        title: 'Case opened',
        description: title
          ? `The case "${title}" was created and is ready for document collection.`
          : 'The case was created and is ready for document collection.',
        category: 'Case',
        tone: 'info',
        icon: Activity,
        actorLabel: actor,
      }
    }
    case 'document.uploaded': {
      const filename = getString(payload, 'filename')
      return {
        title: 'Document uploaded',
        description: filename
          ? `${filename} was added to the case and is ready for document review.`
          : 'A document was added to the case and is ready for document review.',
        category: 'Document',
        tone: 'info',
        icon: FileText,
        actorLabel: actor,
      }
    }
    case 'document.removed': {
      const removedCount = getNumber(payload, 'removed_count')
      const filenames = Array.isArray(payload.filenames)
        ? payload.filenames.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        : []

      return {
        title: removedCount && removedCount > 1 ? 'Uploaded files removed' : 'Uploaded file removed',
        description:
          filenames.length === 1
            ? `${filenames[0]} was removed from the case.`
            : removedCount && removedCount > 1
              ? `${pluralize(removedCount, 'uploaded file')} were removed from the case.`
              : 'An uploaded file was removed from the case.',
        category: 'Document',
        tone: 'warning',
        icon: FileText,
        actorLabel: actor,
      }
    }
    case 'ocr.completed': {
      const filename = getString(payload, 'filename')
      const documentType = getString(payload, 'detected_document_type')
      const documentLabel = humanizeDocumentType(documentType)

      return {
        title: documentType ? `Document identified as ${documentLabel}` : 'Uploaded document reviewed',
        description: documentType
          ? `${filename ?? 'The uploaded document'} was identified as ${documentLabel}. Information from the document was added to the linked case record where possible.`
          : `${filename ?? 'The uploaded document'} was reviewed and is ready for follow up in the case record.`,
        category: 'Document review',
        tone: 'success',
        icon: Sparkles,
        actorLabel: actor,
      }
    }
    case 'structured_document.saved': {
      const documentType = getString(payload, 'document_type')
      const status = getString(payload, 'status')
      const documentLabel = humanizeDocumentType(documentType)

      const title =
        status === 'complete'
          ? `${documentLabel} marked complete`
          : `${documentLabel} updated`
      const description =
        status === 'complete'
          ? `The ${documentLabel} record was updated and marked complete.`
          : status === 'needs_attention'
            ? `The ${documentLabel} record was updated, but it still needs attention before review can continue.`
            : `The ${documentLabel} record was updated in the case workspace.`

      return {
        title,
        description,
        category: 'Document',
        tone: status === 'needs_attention' ? 'warning' : 'success',
        icon: FileText,
        actorLabel: actor,
      }
    }
    case 'case.marked_ready':
      return {
        title: 'Case marked ready for submission',
        description: 'The case was marked ready after the required documents were prepared.',
        category: 'Case status',
        tone: 'success',
        icon: CheckCircle2,
        actorLabel: actor,
      }
    case 'awa.review_initiated':
      return {
        title: 'AWA review started',
        description: reviewSummary(payload),
        category: 'Review',
        tone: 'info',
        icon: PlayCircle,
        actorLabel: actor,
      }
    case 'awa.findings_raised': {
      const openCount = getNumber(payload, 'open_findings') ?? 0
      const highCount = getNumber(payload, 'high_priority_findings') ?? 0

      return {
        title: 'AWA flagged items for follow up',
        description:
          openCount > 0
            ? `AWA identified ${pluralize(openCount, 'item')} that may need attention${highCount > 0 ? `, including ${pluralize(highCount, 'high priority issue')}` : ''}.`
            : 'AWA completed review and did not identify any open issues.',
        category: 'Review',
        tone: highCount > 0 ? 'warning' : 'info',
        icon: AlertTriangle,
        actorLabel: actor,
      }
    }
    case 'transmission.sent': {
      const receipt = getString(payload, 'ace_receipt_id')
      return {
        title: 'Submission sent to ACE',
        description: receipt
          ? `The case submission was sent to ACE and receipt ${receipt} was recorded.`
          : 'The case submission was sent to ACE and a receipt was recorded.',
        category: 'Submission',
        tone: 'success',
        icon: SendHorizonal,
        actorLabel: actor,
      }
    }
    default:
      return {
        title: 'Case updated',
        description: 'A case update was recorded in the activity timeline.',
        category: 'Case',
        tone: 'neutral',
        icon: Activity,
        actorLabel: actor,
      }
  }
}
